import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", icon: "ri-home-5-line" },
  { to: "/category/movie", label: "电影", icon: "ri-film-line" },
  { to: "/category/tv", label: "电视剧", icon: "ri-tv-2-line" },
  { to: "/search", label: "搜索", icon: "ri-search-line" },
  { to: "/about", label: "关于", icon: "ri-information-line" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?query=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur-md"
          : "border-transparent bg-background-50/80 backdrop-blur-sm"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-18 md:px-6">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2.5 cursor-pointer"
          aria-label="红狐影视首页"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
              红狐影视
            </span>
            <span className="mt-0.5 text-[11px] font-medium tracking-wide text-primary-600">
              RED FOX FILM
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-full max-w-xs items-center md:flex lg:max-w-sm"
        >
          <div className="relative w-full">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、导演…"
              className="w-full rounded-md border border-background-200 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none"
              aria-label="搜索影视作品"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-700 md:ml-2 lg:hidden cursor-pointer"
          aria-label="打开导航菜单"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4 md:hidden">
            <div className="relative w-full">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、电视剧、导演…"
                className="w-full rounded-md border border-background-200 bg-background-100 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                aria-label="搜索影视作品"
              />
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "bg-background-100 text-foreground-700"
                  }`
                }
              >
                <i className={`${item.icon} text-lg`}></i>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}